import React, { useCallback, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { ArrowLeft, AlertTriangle, Info } from 'lucide-react';
import { useCreateIncidentMutation } from '@@/services/incident';
import SelectPropertiesInput from '@@/components/Inputs/SelectPropertiesInput';
import SelectPropertyResidentsInput from '@@/components/Inputs/SelectPropertyResidentsInput';
import MultiSelectInput from '@@/components/Inputs/MultiSelectInput';
import StandardInput from '@@/components/Inputs/StandardInput';
import DatePickerInput from '@@/components/Inputs/DatePickerInput';
import CheckboxInput from '@@/components/Inputs/CheckboxInput';
import { embedPath, isEmbedded } from '@@/utils/embed';

import { useForm, FieldError } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';

import { incidentTypes, selectIncidentStatuses } from '@@/constants/incident';

const schema = yup
  .object({
    property: yup
      .object({
        id: yup.string().required(),
        label: yup.string(),
      })
      .required(),
    user: yup
      .object({
        id: yup.string().required(),
        label: yup.string(),
      })
      .required(),
    type: yup
      .object({
        id: yup.string().required(),
        label: yup.string(),
      })
      .required(),
    description: yup.string().trim().min(2).required(),
    status: yup
      .object({
        id: yup.string().required(),
        label: yup.string(),
      })
      .required(),
    date: yup.date(),
    area: yup.string().required(),
    involveCommonPortions: yup.boolean(),
    shouldNotifyPropertyAdmin: yup.boolean(),
  })
  .required();

type FormType = {
  property: {
    id: string;
    label: string;
  };
  user: {
    id: string;
    label: string;
  };
  type: {
    id: string;
    label: string;
  };
  description: string;
  status: {
    id: string;
    label: string;
  };
  date: Date;
  area: string;
  involveCommonPortions: boolean;
  shouldNotifyPropertyAdmin: boolean;
};

const NewIncidentPage: React.FC = () => {
  const navigate = useNavigate();
  const [createIncident, { isSuccess }] = useCreateIncidentMutation();

  const form = useForm<FormType>({
    resolver: yupResolver(schema),
    mode: 'onBlur',
  });

  const [wProperty] = form.watch(['property']);

  const onSubmit = useCallback(
    (data: FormType) => {
      const { property, user, ...body } = data;
      createIncident({
        ...body,
        userId: data.user.id,
        propertyId: data.property.id,
        status: data.status.id,
        type: data.type.id,
      });
    },
    [createIncident],
  );

  useEffect(() => {
    if (isSuccess) {
      form.reset();
      toast.success("L'incident a bien été créé", {
        position: 'top-right',
      });
      navigate(embedPath('/incidents'));
    }
  }, [isSuccess, form.reset, navigate]);

  return (
    <div className={`bg-neutral-50 w-full${isEmbedded() ? '' : ' min-h-screen'}`}>
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-neutral-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-neutral-900">
                Déclarer un nouvel incident
              </h1>
              <p className="mt-1 text-sm text-neutral-600">
                Créez un nouveau rapport d'incident pour une résidence
              </p>
            </div>
            <Link
              className="inline-flex items-center px-4 py-2 text-sm font-medium text-neutral-700 bg-white border border-neutral-300 rounded-button hover:bg-neutral-50 hover:text-neutral-900 transition-colors duration-200 shadow-sm"
              to={embedPath('/incidents')}
            >
              <ArrowLeft className="w-5 h-5 mr-2" />
              Retour
            </Link>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="space-y-8">
          {/* Section 1: Sélection de la résidence et utilisateur */}
          <div className="bg-white rounded-xl shadow-sm border border-neutral-200 p-6">
            <div className="mb-6">
              <h2 className="text-lg font-semibold text-neutral-900 mb-2">
                Résidence et utilisateur
              </h2>
              <div className="bg-warning-50 border-l-4 border-warning-400 p-4 rounded-r-lg mb-4">
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <AlertTriangle className="h-5 w-5 text-warning-600" />
                  </div>
                  <div className="ml-3">
                    <p className="text-sm text-warning-700">
                      <strong>Important :</strong> L'incident sera déclaré au
                      nom de l'utilisateur sélectionné. Assurez-vous de choisir
                      la personne concernée par le sinistre.
                    </p>
                  </div>
                </div>
              </div>
              <p className="text-sm text-neutral-600">
                Sélectionnez la résidence et l'utilisateur concerné par
                l'incident
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <SelectPropertiesInput
                id="property"
                label="Résidence"
                placeholder="Choisissez une résidence (*)"
                error={form.formState.errors.property as FieldError}
                control={form.control}
                isMulti={false}
                className="w-full"
              />

              {!!wProperty && (
                <div className="animate-fade-in">
                  <div className="relative">
                    <SelectPropertyResidentsInput
                      id="user"
                      label="Utilisateur concerné par l'incident"
                      placeholder="Choisissez un utilisateur (*)"
                      propertyId={wProperty.id}
                      error={form.formState.errors.user as FieldError}
                      control={form.control}
                      isMulti={false}
                      className="w-full"
                    />
                    <div className="mt-2 flex items-center text-xs text-primary-600">
                      <Info className="h-4 w-4 mr-1 flex-shrink-0" />
                      Déclarant de l'incident
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Section 2: Détails de l'incident */}
          <div className="bg-white rounded-xl shadow-sm border border-neutral-200 p-6">
            <div className="mb-6">
              <h2 className="text-lg font-semibold text-neutral-900 mb-2">
                Détails de l'incident
              </h2>
              <p className="text-sm text-neutral-600">
                Renseignez les informations relatives à l'incident
              </p>
            </div>

            <div className="space-y-6">
              {/* Type et Status */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <MultiSelectInput
                  control={form.control}
                  id="type"
                  label="Type d'incident (*)"
                  placeholder="Choisissez un type"
                  error={form.formState.errors.type as FieldError}
                  options={incidentTypes}
                  isMulti={false}
                  className="w-full"
                />

                <MultiSelectInput
                  control={form.control}
                  id="status"
                  label="Statut de l'incident (*)"
                  placeholder="Choisissez un statut"
                  error={form.formState.errors.status as FieldError}
                  options={selectIncidentStatuses}
                  isMulti={false}
                  className="w-full"
                />
              </div>

              {/* Description */}
              <div>
                <StandardInput
                  register={form.register}
                  id="description"
                  label="Description (*)"
                  type="textarea"
                  placeholder="Décrivez l'incident en détail..."
                  error={form.formState.errors.description}
                  className="w-full"
                />
              </div>

              {/* Date et Lieu */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <DatePickerInput
                  id="date"
                  control={form.control}
                  showTimeSelect
                  label="Date du sinistre"
                />

                <StandardInput
                  register={form.register}
                  id="area"
                  label="Lieu de survenance (*)"
                  type="textarea"
                  placeholder="Lieu de survenance du sinistre"
                  error={form.formState.errors.area}
                  className="w-full"
                />
              </div>

              {/* Options */}
              <div className="space-y-4 pt-4 border-t border-neutral-200">
                <h3 className="text-sm font-medium text-neutral-900 mb-3">
                  Options
                </h3>

                <div className="space-y-3">
                  <div className="flex items-center p-3 bg-primary-50 rounded-lg">
                    <CheckboxInput
                      register={form.register}
                      id="involveCommonPortions"
                      label="Le sinistre touche les parties communes"
                    />
                  </div>

                  <div className="flex items-center p-3 bg-warning-50 rounded-lg">
                    <CheckboxInput
                      register={form.register}
                      id="shouldNotifyPropertyAdmin"
                      label="Notifier les administrateurs de la résidence"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end space-x-4 pt-6">
            <Link
              to={embedPath('/incidents')}
              className="inline-flex items-center px-6 py-3 text-sm font-medium text-neutral-700 bg-white border border-neutral-300 rounded-button hover:bg-neutral-50 transition-colors duration-200 shadow-sm"
            >
              Annuler
            </Link>

            <button
              className="inline-flex items-center px-6 py-3 text-sm font-medium text-white bg-danger-600 hover:bg-danger-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-danger-500 rounded-button transition-colors duration-200 shadow-button disabled:opacity-50 disabled:cursor-not-allowed"
              onClick={form.handleSubmit(onSubmit)}
              disabled={form.formState.isSubmitting}
            >
              {form.formState.isSubmitting ? (
                <>
                  <svg
                    className="animate-spin -ml-1 mr-3 h-4 w-4 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  Création...
                </>
              ) : (
                "Créer l'incident"
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NewIncidentPage;
